/* eslint-disable @typescript-eslint/no-unused-vars */
import { get, groupBy, isArray } from "lodash";
import { useEffect, useState } from "react";
// material
import { useAppSelector } from "@hooks";
import {
  Box,
  Card,
  LinearProgress,
  Skeleton,
  Table as T,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import Text from "../Text";
import Row from "./partials/Row";
import TableHeader from "./partials/TableHeader";
import TableToolbar from "./partials/TableToolbar";

export interface TableProps {
  className?: string;
  size?: "small" | "medium" | undefined;
  headers: Array<any>; // type ları yazılacak
  actions?: Array<any>;
  children?: any;
  rowActions?: any;
  title?: string | any;
  data: any;
  loading?: boolean;
  error?: Object | string;
  pagination?: boolean;
  checkbox?: boolean;
  length?: number;
  disabled?: any;
  handlePagination?: any;
  limit?: number;
  page?: number;
  total?: number;
  collapsable?: boolean;
  collapseComponent?: any;
  defaultRowActions?: boolean;
  onRowDelete?: any;
  onRowEdit?: any;
  onAdd?: any;
  defaultGroups?: Array<string>;
  disablePagination?: boolean;
  groupable?: boolean;
  disableRowFilter?: boolean;
  disableFilter?: boolean;
  exportation?: boolean;
}

const Table = (props: TableProps) => {
  const {
    className,
    size = props.size || "small",
    headers,
    actions,
    rowActions,
    title,
    data,
    loading,
    checkbox = false,
    pagination = false,
    disabled,
    handlePagination,
    limit,
    page,
    total,
    collapsable = false,
    collapseComponent,
    defaultRowActions,
    onRowDelete,
    onRowEdit,
    onAdd,
    defaultGroups,
    groupable = false,
    disablePagination = false,
    disableRowFilter = false,
    disableFilter = false,
    exportation = false,
  } = props;

  const [_page, setPage] = useState(page || 0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]) as any;
  const [orderBy, setOrderBy] = useState("name");
  const [filterQuery, setFilterQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(
    pagination ? limit || 50 : 100
  );
  const [_data, _setData] = useState(data);
  const language = useAppSelector((state) => state.common.language);

  const [filterColumn, setFilterColumn] = useState<any>({
    query: "",
    column: "",
  });

  const [dragItem, setDragItem] = useState(null);
  const [groupItems, setGroupItems] = useState([]) as any;

  const defaultLabelDisplayedRows = ({
    from,
    to,
    count,
  }: {
    from: number;
    to: number;
    count: number;
  }) => {
    return language === "tr"
      ? `Toplam ${
          count !== -1 ? count : to
        } kayıttan ${from} - ${to} arasındakiler gösteriliyor`
      : `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
  };

  useEffect(() => {
    setSelected([]);
    _setData(data);
    setGroupItems(defaultGroups || []);
  }, [data, defaultGroups]);

  const handleRequestSort = (property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: any) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n: any) => n.id) as Array<any>;
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (event: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: any = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
    handlePagination && handlePagination({ limit: rowsPerPage, page: newPage });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    handlePagination &&
      handlePagination({
        limit: parseInt(event.target.value, 10),
        page: _page,
      });
  };

  const onFilter = (event: any) => {
    setFilterQuery(event.target.value);
  };

  const groupArray = (array: any, groupItem: any): any => {
    const t = array.map((r: any) => {
      const grouped: any = groupBy(r.data, (item: any) => item[groupItem]) // prettier-ignore
      const _temp = Object.keys(grouped).map((key: string) => ({category:groupItem, key, data: grouped[key] })) // prettier-ignore
      return {
        ...r,
        key: r.key,
        data: _temp,
        hasGroup: true,
      };
    });
    return t;
  };

  function descendingComparator(a: any, b: any, orderBy: any) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order: any, orderBy: any) {
    return order === "desc"
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  }

  function applySortFilter(
    array: [any],
    comparator: any,
    query: any,
    groupItems: any
  ) {
    let stabilizedThis = isArray(array)
      ? array?.map((el, index) => [el, index] as [any, number])
      : [];

    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });

    const sortedArray = stabilizedThis.map((el) => el[0]);

    if (filterColumn.query) {
      groupItems.length > 0 && setGroupItems([]);
      return array.filter((row: any) =>
        row?.[filterColumn?.column]
          ?.toString()
          .toLowerCase()
          .includes(filterColumn.query)
      );
    }

    if (query) {
      groupItems.length > 0 && setGroupItems([]);
      return array.filter((row: any) =>
        Object.entries(row).some((s: any) =>
          String(s[1]).toLowerCase().includes(query.toLowerCase())
        )
      );
    }

    let groupedData = [] as any;

    // TODO burası nested for döngüsü ile düzenlenecek
    if (groupItems?.length > 0) {
      groupItems.forEach((groupItem: string, index: number) => {
        switch (index) {
          case 0:
            const grouped: any = groupBy(sortedArray, (item: any) => item[groupItem]) // prettier-ignore
            const _temp = Object.keys(grouped).map((key: string) => ({ key, category:groupItem, data: grouped[key] })) // prettier-ignore
            groupedData = _temp;
            break;
          case 1:
            groupedData = groupArray(groupedData, groupItem);
            break;
          case 2:
            const a = groupedData.map((y: any) => {
              const t = groupArray(y.data, groupItem);
              return { ...y, key: y.key, data: t };
            });
            groupedData = a;
            break;
          case 3:
            const x = groupedData.map((y: any) => {
              const data = y.data.map((z: any) => {
                const temp = groupArray(z.data, groupItem);
                return { ...z, key: z.key, data: temp };
              });

              return { ...y, key: y.key, data };
            });
            groupedData = x;
            break;
          default:
            break;
        }
      });

      return groupedData;
    }

    return sortedArray;
  }

  const getElement = (rowElement: any, index: number, row: any) => {
    if (rowElement?.slot && rowElement.id !== "" && row) {
      let SlotElement;

      if (props?.children?.length > 0) {
        SlotElement = props?.children?.find(
          (s: any) => s?.props?.id === rowElement?.id
        )?.props?.children;
      } else {
        SlotElement = props?.children?.props?.children;
      }

      return (
        <TableCell key={index} {...rowElement?.props}>
          {SlotElement(get(row, rowElement?.id), row) ?? null}
        </TableCell>
      );
    } else {
      if (rowElement.id !== "") {
        switch (rowElement.type) {
          case "date":
            return (
              <TableCell
                sx={{ fontWeight: "bold" }}
                key={index}
                {...rowElement?.props}
              >
                {moment(row[rowElement.id]).format("DD MMM YYYY")}
              </TableCell>
            );

          default:
            return (
              <TableCell key={index} {...rowElement?.props}>
                {row[rowElement.id]}
              </TableCell>
            );
        }
      }
    }
  };

  const onFilterByColumn = (text: any, column: any) => {
    setFilterColumn({
      column,
      query: text,
    });
  };

  const filteredData = applySortFilter(
    data,
    getComparator(order, orderBy),
    filterQuery,
    groupItems
  );
  const notFound = filteredData.length === 0;

  return (
    <>
      <Card className={className}>
        <TableToolbar
          title={title}
          numSelected={selected.length}
          selecteds={selected}
          filterQuery={filterQuery}
          onFilter={onFilter}
          actions={actions}
          rowActions={rowActions}
          dragItem={dragItem}
          setDragItem={setDragItem}
          groupItems={groupItems}
          setGroupItems={setGroupItems}
          headers={headers}
          defaultRowActions={defaultRowActions}
          onAdd={onAdd}
          groupable={groupable}
          disableFilter={disableFilter}
          data={data}
          exportation={exportation}
        />

        {disabled && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={"100%"}
              animation={false}
              sx={{
                bgcolor: "rgba(255,255,255,0.7)",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
              }}
            ></Skeleton>
            <Typography sx={{ mt: 5, zIndex: 999 }} variant="h3">
              {disabled}
            </Typography>
          </Box>
        )}
        {loading && <LinearProgress sx={{ height: 5 }} color="primary" />}
        <TableContainer sx={{ minWidth: 800 }}>
          <T size={size} aria-label="collapsible table">
            <TableHeader
              groupable={groupable}
              checkbox={checkbox}
              collapsable={collapsable}
              order={order}
              orderBy={orderBy}
              headLabel={headers}
              rowCount={data.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              dragItem={dragItem}
              groupItems={groupItems}
              setGroupItems={setGroupItems}
              setDragItem={setDragItem}
              onFilterByColumn={onFilterByColumn}
              disableRowFilter={disableRowFilter}
            />
            {!loading && (
              <TableBody>
                {filteredData
                  ?.slice(
                    _page * rowsPerPage,
                    _page * rowsPerPage + rowsPerPage
                  )
                  ?.map((row: any, index: any) => {
                    const { id } = row;
                    const isItemSelected = selected.indexOf(id) !== -1;

                    return (
                      <Row
                        key={index}
                        id={id}
                        collapsable={collapsable}
                        isItemSelected={isItemSelected}
                        checkbox={checkbox}
                        handleCheckboxClick={handleCheckboxClick}
                        headers={headers}
                        rowIndex={index}
                        row={row}
                        getElement={getElement}
                        rowActions={rowActions}
                        collapseComponent={collapseComponent}
                        groupItems={groupItems}
                        defaultRowActions={defaultRowActions}
                        onRowDelete={onRowDelete}
                        onRowEdit={onRowEdit}
                      />
                    );
                  })}
              </TableBody>
            )}

            {loading && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={headers?.length}>
                    <Text>loading</Text>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            {!loading && notFound && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={headers?.length}>
                    <Box
                      sx={{
                        minHeight: 80,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Text variant="h6">not_found</Text>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </T>
        </TableContainer>

        {!disablePagination &&
          (pagination ? (
            <TablePagination
              rowsPerPageOptions={[30, 40, 50, 100, 150, 200]}
              component="div"
              count={total || 0}
              rowsPerPage={rowsPerPage}
              page={_page}
              onPageChange={handleChangePage}
              labelDisplayedRows={defaultLabelDisplayedRows}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ) : (
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={_page}
              labelDisplayedRows={defaultLabelDisplayedRows}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ))}
      </Card>
    </>
  );
};

export default Table;
